import {
	div, button, ul, li, label, textarea
} from 'iblokz-snabbdom-helpers';
import {fromEvent} from 'rxjs';
import {map, takeUntil, debounceTime} from 'rxjs';

import md from '../../../util/md';
import caret from '../../../util/caret';
import {fieldLabel} from './label';

const getParent = (el, tagName) => ([].concat(tagName).indexOf(el.tagName) === -1)
	? getParent(el.parentNode, tagName) : el;

const isEditableFocused = elm =>
	elm === document.activeElement || elm.contains(document.activeElement);

const applyHtml = (elm, content) => {
	const html = md.toHTML(content || '<p><br></p>');
	if (elm.innerHTML !== html) elm.innerHTML = html;
};

// innerHTML via hooks (not props) so contenteditable DOM mutations do not
// desync Snabbdom's vnode children — also fixes mode-switch stacking a
// second textarea under the rich-edit area.
export default ({field, value: {content = '', pos = {
	start: {row: 0, col: 0},
	end: {row: 0, col: 1}
}, mode = 'wysiwyg'} = {}, update = () => {}, focused = false, addons = [], i18n = {},
	langSwitcher = null, contentKey = ''
}) => (
	div('.field', [
		field.name.split('.').length === 1 ? label([
			fieldLabel(i18n, field.name.split('.').pop()),
			': '
		]) : '',
		div({
			key: `wysiwyg-root-${field.name}-${contentKey}`,
			class: {wysiwyg: true},
			attrs: {'data-mode': mode}
		}, [
			div('.toolbox', [].concat(
				ul(`.left`, [].concat(
						li(button('.btn.btn-white.fa.fa-bold', {
							on: {click: ev => (ev.preventDefault(), document.execCommand('bold'))}})),
						li(button('.btn.btn-white.fa.fa-italic', {
							on: {click: ev => (ev.preventDefault(), document.execCommand('italic'))}})),
						li(button('.btn.btn-white.fa.fa-underline', {
							on: {click: ev => (ev.preventDefault(), document.execCommand('underline'))}})),
						li('  '),
						li(button('.btn.btn-white.fa.fa-list-ul', {
							on: {click: ev => (ev.preventDefault(), document.execCommand('insertUnorderedList'))}})),
						li(button('.btn.btn-white.fa.fa-list-ol', {
							on: {click: ev => (ev.preventDefault(), document.execCommand('insertOrderedList'))}})),
						li('  '),
						li(button('.btn.btn-white.fa.fa-outdent', {
							on: {click: ev => (ev.preventDefault(), document.execCommand('outdent'))}})),
						li(button('.btn.btn-white.fa.fa-indent', {
							on: {click: ev => (ev.preventDefault(), document.execCommand('indent'))}}))
					)),
					ul('.right', [].concat(
						li(button(`.btn.btn-white.fa.fa-code`, {
							class: {active: mode === 'markdown'},
							on: {click: ev => (ev.preventDefault(), update(field.name.split('.'), {content, pos,
								mode: mode === 'wysiwyg' ? 'markdown' : 'wysiwyg'
							}))}
						})),
						addons.map(addon =>
							li(button(`.btn.btn-white.fa.${addon.icon}[type="button"]`, {
								on: {
									click: ev => addon.cb(field, {content: md.fromHTML(getParent(ev.target, 'DIV').parentNode
										.querySelector('.wysiwyg').innerHTML)}
									)
								}
							}))
						),
						langSwitcher
							? li('.field-lang-switch-wrap', langSwitcher)
							: []
					))
			)),
			div({
				key: `wysiwyg-${field.name}-${contentKey}-${mode}`,
				attrs: {contenteditable: 'true'},
				hook: {
					insert: ({elm}) => {
						applyHtml(elm, content);
						if (mode === 'wysiwyg' && focused) {
							caret.set(elm, pos, 'p, li, div, h1, h2, h3, h4, h5');
						}
					},
					update: (oldVnode, {elm}) => {
						// While typing, DOM is source of truth — rewriting/caret fights selection
						if (!isEditableFocused(elm)) {
							applyHtml(elm, content);
						}
					}
				},
				on: {
					focus: ({target}) => fromEvent(target, 'input').pipe(
						map(ev => ev.target),
						takeUntil(fromEvent(target, 'blur')),
						debounceTime(500)
					).subscribe(el =>
						update(
							field.name.split('.'),
							{
								content: md.fromHTML(el.innerHTML),
								pos: caret.get(el, 'p, li, div, h1, h2, h3, h4, h5'),
								mode
							}
						)
					)
				}
			}),
			textarea({
				key: `md-${field.name}-${contentKey}-${mode}`,
				attrs: langSwitcher ? {} : {name: field.name || field},
				hook: {
					insert: ({elm}) => {
						elm.value = content || '';
					},
					update: (oldVnode, {elm}) => {
						if (document.activeElement !== elm && elm.value !== (content || '')) {
							elm.value = content || '';
						}
					}
				},
				on: {
					focus: ({target}) => fromEvent(target, 'input').pipe(
						map(ev => ev.target),
						takeUntil(fromEvent(target, 'blur')),
						debounceTime(500)
					).subscribe(({value}) => update(
						field.name.split('.'),
						{
							content: value,
							pos,
							mode
						}
					))
				}
			})
		])
	])
);
